import React, { useCallback, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useConvertToAgreement } from "../../hooks/useConvertToAgreement";
import { ConvertToAgreementDialog } from "../../components/ConvertToAgreementDialog";
import { Panel } from "@poseidon/ui/components/Panel";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import LoadingButton from "@mui/lab/LoadingButton";
import { usePermissions } from "@poseidon/ui/components";
import { ApplicationHasAgreementWarning } from "../../components/ApplicationHasAgreementWarning";
import { isApplicationEditable } from "../../isApplicationEditable";
import { doc } from "firebase/firestore";
import { db } from "@poseidon/firebase";
import { updateDoc } from "firebase/firestore";

const useChangeStatus = (applicationId) => {
  const docRef = applicationId
    ? doc(db, "applications", applicationId)
    : undefined;
  const [saving, setSaving] = useState(false);
  const [error, setSaveError] = useState(undefined);
  const setStatus = useCallback(
    async (status) => {
      setSaving(true);
      try {
        await updateDoc(docRef, {
          status,
        });
        setSaveError(undefined);
        setSaving(false);
        return true;
      } catch (error) {
        setSaveError(error);
        setSaving(false);
        return false;
      }
    },
    [docRef, setSaving, setSaveError]
  );

  return {
    saving,
    error,
    setStatus,
  };
};
export const DangerZone = ({ application }) => {
  const { canWrite } = usePermissions();
  const convertToAgreement = useConvertToAgreement(application.id);
  const changeStatus = useChangeStatus(application.id);
  const readonly = !isApplicationEditable(application);
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const [convertOpen, setConvertOpen] = useState(false);
  const handleConvertToAgreement = (options) => {
    convertToAgreement.convertToAgreement(options).then((success) => {
      setConvertOpen(false);
      if (success) {
        setToast("Application has been converted to an Agreement");
      } else {
        setError("Error converting Application to an Agreement");
      }
    });
  };
  const handleCloseApplication = () => {
    changeStatus.setStatus("closed");
  };
  const handleReopenApplication = () => {
    changeStatus.setStatus("active");
  };
  return (
    <Panel expandable startCollapsed={true} title="Danger Zone">
      <ApplicationHasAgreementWarning
        application={application}
        sx={{ mb: 3 }}
      />
      <Typography variant="subtitle" color="textSecondary">
        Use these options with care
      </Typography>

      <Stack spacing={2} alignItems="start">
        {application.status === "active" && (
          <LoadingButton
            variant="contained"
            style={{ margin: "10px 0", minWidth: "200px" }}
            loading={changeStatus.saving}
            disabled={!canWrite}
            onClick={handleCloseApplication}
          >
            Close application
          </LoadingButton>
        )}
        {application.status !== "active" && application.status !== "agreement" && (
          <LoadingButton
            variant="contained"
            style={{ margin: "10px 0", minWidth: "200px" }}
            loading={changeStatus.saving}
            disabled={!canWrite}
            onClick={handleReopenApplication}
          >
            Re-Open application
          </LoadingButton>
        )}
        <LoadingButton
          variant="contained"
          style={{ margin: "10px 0", minWidth: "200px" }}
          loading={
            convertToAgreement.saving ||
            (convertToAgreement.success && !readonly)
          }
          disabled={convertToAgreement.success || readonly || !canWrite}
          onClick={() => setConvertOpen(true)}
        >
          Convert to agreement
        </LoadingButton>
      </Stack>
      <ConvertToAgreementDialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        onConfirm={handleConvertToAgreement}
        application={application}
        saving={convertToAgreement.saving}
      />
    </Panel>
  );
};
